// External libraries
import { action } from 'easy-peasy';

// Internal modules
import { features } from '@/lib/features';
import { getParams } from '@/lib/utils/params';
import { CARD_FIELDS_TO_DISPLAY } from '../constants';
import type { FiTypes } from '@/types';
import type { UiActions, UiModel, UiState } from './types';
import type { IncentiveOfferState } from '../types';
import { SPINNER_TYPES } from '../constants';
import { getLocale } from '@/lib/globals/globals';
import { CI } from '@/lib/customerInteractions';
import { CountryCodes } from '@/generated/graphql';

/**
 * UI MODEL
 * Contains data that relates to the state of the UI
 */

const getInitialFundingInstrumentType = (): FiTypes => {
  const { country } = getLocale();
  const hasBank = features.someActive('hasAddBank', 'hasConsumerAddBank');
  const isCardTabDefault = country === CountryCodes.Jp;
  const isBankDefaultFiType = hasBank && !isCardTabDefault;

  return isBankDefaultFiType ? 'bank' : 'card';
};

const isTabFeatureEnabled = (): boolean => {
  const bnplExperience = features.get('bnplExperience');
  const hasPayLaterTab = bnplExperience === 'TAB';
  const hasBank = features.someActive('hasAddBank', 'hasConsumerAddBank');
  const isBoltActive = features.isActive('blockDomesticPayers');

  // Bolt UI is only implemented on PayNowForm so other tabs aren't supported
  return (hasPayLaterTab || hasBank) && !isBoltActive;
};

const getInitialIncentiveOfferState = (): IncentiveOfferState => {
  return {
    shouldDisplayOffer: false,
    shouldDisplayOfferAccepted: false,
  };
};

const getWeasleyTheme = (): string => {
  const hasPPReactStyles = features.isActive('isPPReactStylesEligible');
  return hasPPReactStyles ? 'ppReactTheme' : 'controlTheme';
};

const getUiState = (overrides: Partial<UiState> = {}): UiState => ({
  // State
  addressNormalizationState: {
    page: 'shipToBilling',
    showBackButton: false,
    showNextButton: false,
    showSubmitButton: false,
  },
  cardFieldsToDisplay: CARD_FIELDS_TO_DISPLAY,
  checkForMemberBenefits: false,
  continueToAutoReturn: false,
  country: getLocale().country,
  countToggles: 0,
  currentLanguage: '',
  fiType: getInitialFundingInstrumentType(),
  genericError: undefined,
  hasCardTypeSelector: features.isActive('hasCardTypeSelector'),
  hasCupOneTimePasswordConfirmationMessage: false,
  // At ay point has the user focused into the form
  hasFormBeenFocused: false,
  identityAuthProviderStatus: 'notChecked',
  incentiveOfferState: getInitialIncentiveOfferState(),
  isAddressNormalizeOpen: false,
  isAutoSmsNotified: false,
  isBlockedDomesticTransaction: false,
  isCRSInterstitialOpen: false,
  isFILimitExceededInterstitialOpen: false,
  isFormSubmitting: false,
  isInstallmentButtonShown: false,
  isInstallmentInterstitialShown: false,
  isInventorySoldOutOpen: false,
  isMemberBenefitsInterstitialOpen: false,
  isMemberBenefitsTrueGuestsInterstitialOpen: false,
  isMemberBenefitsTrueGuestsInterstitialReject: false,
  isOneTimePasswordConfirmationOpen: false,
  isOverCaptureVisible: false,
  isPageLoading: false,
  isPasswordlessUpgradeOpen: false,
  isPhoneConfirmationOpen: false,
  isReduxStoreHydrated: false,
  isResidentialAddressOpen: false,
  isRiskPhoneConfirmation: false,
  isSepaInterstitialOpen: false,
  isShippingAndTaxLoaded: false,
  isShippingShown: false,
  isSubmitting: false,
  isThreeDS2Shown: false,
  isUpgradeErrorShown: false,
  isUpgradeInterstitialOpen: false,
  isWPSDonePageOpen: false,
  multiBrandCardTypes: [],
  pageLevelError: '',
  pageWarningMessage: '',
  phoneConfirmationAlertType: '',
  showCardConversionOptions: false,
  showIsExistingAccountEmailMessage: false,
  showLoadingSpinnerWithMessage: null,
  showPasskeyEnrollmentPage: false,
  showTabs: isTabFeatureEnabled(),
  showVenmoMessage: getParams().fundingSource === 'venmo',
  soldOutUrlFromError: undefined,
  spinnerType: SPINNER_TYPES.empty,
  theme: getWeasleyTheme(),
  ...overrides,
});

const createUiActions = (): UiActions => ({
  // Actions
  // eslint-disable-next-line sort-keys
  setAddressNormalizationState: action((state, payload) => {
    state.addressNormalizationState = payload;
  }),
  setCardFieldsToDisplay: action((state, payload) => {
    state.cardFieldsToDisplay = payload;
  }),
  setCheckForMemberBenefits: action((state, payload) => {
    state.checkForMemberBenefits = Boolean(payload);
  }),
  setContinueToAutoReturn: action((state, payload) => {
    state.continueToAutoReturn = Boolean(payload);
  }),
  setCountToggles: action((state, payload) => {
    state.countToggles = payload;
  }),
  setCurrentLanguage: action((state, payload) => {
    state.currentLanguage = payload;
  }),
  setFiType: action((state, payload) => {
    state.fiType = payload;
  }),
  setGenericError: action((state, payload) => {
    state.genericError = payload;
  }),
  setHasCardTypeSelector: action((state, payload) => {
    state.hasCardTypeSelector = Boolean(payload);
  }),
  setHasCupOneTimePasswordConfirmationMessage: action((state, payload) => {
    state.hasCupOneTimePasswordConfirmationMessage = Boolean(payload);
  }),
  setHasFormBeenFocused: action((state, payload) => {
    state.hasFormBeenFocused = Boolean(payload);
  }),
  setIdentityAuthProviderStatus: action((state, payload) => {
    state.identityAuthProviderStatus = payload;
  }),
  setIncentiveOfferState: action((state, payload) => {
    state.incentiveOfferState = payload;
  }),
  setIsAutoSmsNotified: action((state, payload) => {
    state.isAutoSmsNotified = Boolean(payload);
  }),
  setIsBlockedDomesticTransaction: action((state, payload) => {
    state.isBlockedDomesticTransaction = Boolean(payload);
  }),
  setIsFILimitExceededInterstitialOpen: action((state, payload) => {
    state.isFILimitExceededInterstitialOpen = payload;
  }),
  setIsInstallmentButtonShown: action((state, payload) => {
    state.isInstallmentButtonShown = Boolean(payload);
  }),
  setIsInstallmentInterstitialShown: action((state, payload) => {
    state.isInstallmentInterstitialShown = payload;
  }),
  setIsMemberBenefitsInterstitialOpen: action((state, payload) => {
    state.isMemberBenefitsInterstitialOpen = Boolean(payload);
  }),
  setIsMemberBenefitsTrueGuestsInterstitialReject: action((state, payload) => {
    state.isMemberBenefitsTrueGuestsInterstitialReject = Boolean(payload);
  }),
  setIsOneTimePasswordConfirmationOpen: action((state, payload) => {
    state.isOneTimePasswordConfirmationOpen = Boolean(payload);
  }),
  setIsReduxStoreHydrated: action((state, payload) => {
    state.isReduxStoreHydrated = payload;
  }),
  setIsRiskPhoneConfirmation: action((state, payload) => {
    state.isRiskPhoneConfirmation = Boolean(payload);
  }),
  setIsShippingAndTaxLoaded: action((state, payload) => {
    state.isShippingAndTaxLoaded = payload;
  }),
  setIsShippingShown: action((state, payload) => {
    state.isShippingShown = Boolean(payload);
  }),
  setMultiBrandCardTypes: action((state, payload) => {
    state.multiBrandCardTypes = payload;
  }),
  setOverCaptureVisible: action((state, payload) => {
    state.isOverCaptureVisible = Boolean(payload);
  }),
  setPageLevelError: action((state, payload) => {
    state.pageLevelError = payload;
  }),
  setPageWarningMessage: action((state, payload) => {
    state.pageWarningMessage = payload;
  }),
  setPhoneConfirmationAlertType: action((state, payload) => {
    state.phoneConfirmationAlertType = payload;
  }),
  setShowCardConversionOptions: action((state, payload) => {
    state.showCardConversionOptions = payload;
  }),
  setShowIsExistingAccountEmailMessage: action((state, payload) => {
    state.showIsExistingAccountEmailMessage = payload;
  }),
  setShowLoadingSpinnerWithMessage: action((state, payload) => {
    state.showLoadingSpinnerWithMessage = payload;
  }),
  setShowPasskeyEnrollmentPage: action((state, payload) => {
    state.showPasskeyEnrollmentPage = payload;
  }),
  setShowVenmoMessage: action((state, payload) => {
    state.showVenmoMessage = Boolean(payload);
  }),
  setSoldOutUrlFromError: action((state, payload) => {
    state.soldOutUrlFromError = payload;
  }),
  setSpinnerType: action((state, payload) => {
    state.spinnerType = payload;
  }),
  setTheme: action((state, payload) => {
    state.theme = payload;
  }),
  toggleIsAddressNormalizeOpen: action((state, payload) => {
    if (payload === true) {
      CI.start(CI.Event.CONTINGENCY_ADDRESS_NORMALIZATION_USER_INPUT);
    } else {
      CI.end(CI.Event.CONTINGENCY_ADDRESS_NORMALIZATION_USER_INPUT);
    }
    state.isAddressNormalizeOpen = Boolean(payload);
  }),
  toggleIsCRSInterstitialOpen: action((state, payload) => {
    if (payload === true) {
      CI.start(CI.Event.CONTINGENCY_TAX_INFORMATION_USER_INPUT);
    } else {
      CI.end(CI.Event.CONTINGENCY_TAX_INFORMATION_USER_INPUT);
    }
    state.isCRSInterstitialOpen = Boolean(payload);
  }),
  toggleIsFormSubmitting: action((state, payload) => {
    state.isFormSubmitting = Boolean(payload);
  }),
  toggleIsInventorySoldOutOpen: action((state, payload) => {
    state.isInventorySoldOutOpen = Boolean(payload);
  }),
  toggleIsMemberBenefitsTrueGuestsInterstitialOpen: action((state, _) => {
    state.isMemberBenefitsTrueGuestsInterstitialOpen = Boolean(
      !state.isMemberBenefitsTrueGuestsInterstitialOpen,
    );
  }),
  toggleIsPageLoading: action((state, payload) => {
    state.isPageLoading = Boolean(payload);
  }),
  toggleIsPasswordlessUpgradeOpen: action((state, payload) => {
    state.isPasswordlessUpgradeOpen = Boolean(payload);
  }),
  toggleIsPhoneConfirmationOpen: action((state, payload) => {
    state.isPhoneConfirmationOpen = Boolean(payload);
  }),
  toggleIsResidentialAddressOpen: action((state, payload) => {
    state.isResidentialAddressOpen = Boolean(payload);
  }),
  toggleIsSepaInterstitialOpen: action((state, payload) => {
    state.isSepaInterstitialOpen = Boolean(payload);
  }),
  toggleIsThreeDS2Shown: action((state, payload) => {
    state.isThreeDS2Shown = Boolean(payload);
  }),
  toggleIsUpgradeErrorShown: action((state, payload) => {
    state.isUpgradeErrorShown = Boolean(payload);
  }),
  toggleIsUpgradeInterstitialOpen: action((state, payload) => {
    state.isUpgradeInterstitialOpen = Boolean(payload);
  }),
  toggleIsWPSDonePageOpen: action((state, payload) => {
    state.isWPSDonePageOpen = Boolean(payload);
  }),
});

const uiModelFactory = (): UiModel => ({
  ...getUiState(),
  ...createUiActions(),
});

export { getUiState, uiModelFactory };
